/* global debounce */

if (!customElements.get('predictive-search')) {
  class PredictiveSearch extends HTMLElement {
    constructor() {
      super();
      this.cachedResults = {};
      this.input = this.querySelector('.js-search-input');
      this.productTypeSelect = this.querySelector('.js-search-product-types');
      this.productTypeInput = document.getElementById('product_type_input');
      this.resetBtn = this.querySelector('.js-search-reset');
      this.results = this.querySelector('.js-search-results');
      this.overlay = this.querySelector('.js-search-overlay');
      this.statusEl = this.querySelector('.js-search-status');
      this.loadingText = this.getAttribute('data-loading-text');
      this.addListeners();
      this.injectUsedBadgeStyles();
    }

    disconnectedCallback() {
      this.close();
    }

    addListeners() {
      this.input.addEventListener('focus', this.handleFocus.bind(this));
      this.input.addEventListener('input', debounce(this.handleInput.bind(this)));
      if (this.productTypeSelect) {
        this.productTypeSelect.addEventListener('change', this.handleProductTypeChange.bind(this));
      }
      this.querySelector('.search').addEventListener('submit', this.handleSubmit.bind(this));
    }

    getQuery() {
      return this.input.value.trim();
    }

    handleSubmit(evt) {
      if (
        !this.getQuery().length
        || this.querySelector('.predictive-search__item[aria-selected="true"]:not(.js-submit)')
      ) {
        evt.preventDefault();
      }
    }

    handleInput() {
      this.input.setAttribute('value', this.input.value);
      const searchTerm = this.getQuery();
      if (!searchTerm.length) {
        this.close();
        return;
      }
      this.getResults(searchTerm);
    }

    handleProductTypeChange(evt) {
      this.productTypeInput.value = evt.detail.selectedValue;
      const query = this.getQuery();
      if (query.length > 0) this.getResults(query);
    }

    handleFocus() {
      const searchTerm = this.getQuery();
      if (!searchTerm.length) return;
      if (this.getAttribute('results') === 'true') {
        this.open();
      } else {
        this.getResults(searchTerm);
      }
    }

    handleKeydown(evt) {
      if (evt.target.matches('.tablist__tab')) return;
      switch (evt.key) {
        case 'ArrowUp':
        case 'ArrowDown': {
          evt.preventDefault();
          if (this.hasAttribute('open')) this.handleResultsNav(evt.key);
          break;
        }
        case 'Enter':
          this.selectOption();
          break;
        case 'Escape':
          this.close();
          break;
      }
    }

    handleKeyup() {
      if (!this.getQuery().length) this.close();
    }

    handleResultsNav(key) {
      const selectedResult = this.querySelector(
        '[role="tabpanel"]:not([hidden]) [aria-selected="true"]'
      );
      const allResults = this.querySelectorAll(
        '[role="tabpanel"]:not([hidden]) .predictive-search__item'
      );
      let resultToSelect = allResults[0];

      if (key === 'ArrowUp') {
        if (!selectedResult) return;
        resultToSelect = selectedResult.previousElementSibling || allResults[allResults.length - 1];
      } else if (key === 'ArrowDown') {
        if (selectedResult) {
          resultToSelect = selectedResult.nextElementSibling || allResults[0];
        }
      }

      this.statusEl.textContent = '';

      if (resultToSelect) {
        if (resultToSelect === selectedResult) return;
        resultToSelect.setAttribute('aria-selected', 'true');
        if (selectedResult) selectedResult.setAttribute('aria-selected', 'false');
        this.setLiveRegionText(resultToSelect.textContent);
        this.input.setAttribute('aria-activedescendant', resultToSelect.id);
      }
    }

    selectOption() {
      const selectedResult = this.querySelector('[aria-selected="true"] a[href]');
      if (selectedResult) selectedResult.click();
    }

    async getResults(searchTerm) {
      this.setLiveRegionLoadingState();

      let searchFields = 'title,product_type,variants.title,vendor';
      if (typeof theme !== 'undefined' && theme.settings && theme.settings.pSearchIncludeSkus) {
        searchFields += ',variants.sku';
      }
      // Ensure tags are available for JSON endpoint matching
      searchFields += ',tag';

      let searchParams = '';
      if (this.productTypeInput && this.productTypeInput.value !== '') {
        searchParams = `q=product_type:${encodeURIComponent(this.productTypeInput.value)} AND ${encodeURIComponent(searchTerm)}`;
      } else {
        searchParams = `q=${encodeURIComponent(searchTerm)}`;
      }

      searchParams += `&${encodeURIComponent('resources[limit]')}=${theme.settings.pSearchLimit}`;
      searchParams += `&${encodeURIComponent('resources[limit_scope]')}=${theme.settings.pSearchLimitScope}`;
      searchParams += `&${encodeURIComponent('resources[options][fields]')}=${searchFields}`;
      searchParams += '&section_id=predictive-search';

      const queryKey = searchParams.replace(' ', '-').toLowerCase();
      if (this.cachedResults[queryKey]) {
        this.renderResults(this.cachedResults[queryKey], searchParams);
        return;
      }

      try {
        const response = await fetch(`${theme.routes.predictiveSearch}?${searchParams}`);
        if (!response.ok) throw new Error(response.status);

        const tmpl = document.createElement('template');
        tmpl.innerHTML = await response.text();

        const resultsEl = tmpl.content.querySelector('#shopify-section-predictive-search');
        const resultsMarkup = resultsEl.innerHTML.replace(/psearch/g, this.input.id);

        this.cachedResults[queryKey] = resultsMarkup;
        this.renderResults(resultsMarkup, searchParams);
      } catch (error) {
        this.close();
        throw error;
      }
    }

    setLiveRegionLoadingState() {
      this.setLiveRegionText(this.loadingText);
    }

    setLiveRegionText(statusText) {
      this.statusEl.setAttribute('aria-hidden', 'false');
      this.statusEl.textContent = statusText;
      setTimeout(() => {
        this.statusEl.setAttribute('aria-hidden', 'true');
      }, 1000);
    }

    renderResults(resultsMarkup, searchParamsForJson) {
      this.results.innerHTML = resultsMarkup;
      this.setAttribute('results', true);
      this.open();
      this.decorateUsedReplacingVendor(searchParamsForJson);
    }

    open() {
      this.overlay.classList.add('is-visible');
      if (this.getQuery().length) this.resetBtn.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
      this.setAttribute('open', '');
      document.body.classList.add('overlay-predictive-search');

      this.keydownHandler = this.keydownHandler || this.handleKeydown.bind(this);
      this.keyupHandler = this.keyupHandler || this.handleKeyup.bind(this);
      this.resetBtnClickHandler = this.resetBtnClickHandler || this.close.bind(this);
      this.overlayClickHandler = this.overlayClickHandler || this.close.bind(this);

      this.addEventListener('keydown', this.keydownHandler);
      this.addEventListener('keyup', this.keyupHandler);
      this.resetBtn.addEventListener('click', this.resetBtnClickHandler);
      this.overlay.addEventListener('click', this.overlayClickHandler);
    }

    close(evt) {
      if (evt && evt.target === this.resetBtn) {
        this.input.value = '';
        this.removeAttribute('results');
        this.input.focus();
      }

      const selected = this.querySelector('.predictive-search__item[aria-selected="true"]');
      if (selected) selected.setAttribute('aria-selected', 'false');

      this.removeAttribute('open');
      this.resetBtn.hidden = true;
      this.overlay.classList.remove('is-visible');
      this.input.setAttribute('aria-activedescendant', '');
      this.input.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('overlay-predictive-search');

      this.removeEventListener('keydown', this.keydownHandler);
      this.resetBtn.removeEventListener('click', this.resetBtnClickHandler);
      this.overlay.removeEventListener('click', this.overlayClickHandler);
    }

    injectUsedBadgeStyles() {
      if (document.getElementById('ps-used-badge-style')) return;
      const style = document.createElement('style');
      style.id = 'ps-used-badge-style';
      style.textContent = `
        .ps-used-badge{
          display:inline-block;
          font-size:10px;
          line-height:1;
          padding:4px 8px 4px 8px;
          text-transform:uppercase;
          letter-spacing:.04em;
          font-weight:400;
          background: var(--color-sale, #f97316);
          color: var(--color-sale-contrast,rgb(255, 255, 255));
          white-space:nowrap;
          vertical-align:middle;
        }
      `;
      document.head.appendChild(style);
    }

    /**
     * Collect product anchor + handle for each rendered predictive result.
     */
    getRenderedProductLinks() {
      const links = Array.from(
        this.results.querySelectorAll('a[href*="/products/"]')
      );
      return links
        .map(a => {
          const href = a.getAttribute('href') || '';
          const idx = href.indexOf('/products/');
          if (idx === -1) return null;
          const rest = href.slice(idx + '/products/'.length);
          const handle = rest.split(/[?#/]/)[0];
          if (!handle) return null;
          return { link: a, handle };
        })
        .filter(Boolean);
    }

    /**
     * Replace the vendor text with "Used Item" badge for SC-USED products only.
     */
    async decorateUsedReplacingVendor(searchParamsForJson) {
      const rendered = this.getRenderedProductLinks();
      if (!rendered.length) return;

      const replaceVendorWithBadge = (itemEl, vendorText) => {
        if (!itemEl) return;
        if (itemEl.dataset.usedVendorReplaced === 'true') return;

        // Try common vendor selectors first
        let vendorEl =
          itemEl.querySelector('.predictive-search__item-vendor') ||
          itemEl.querySelector('.card__vendor') ||
          itemEl.querySelector('.product-item__vendor') ||
          itemEl.querySelector('[data-predictive-vendor]');

        // If not found, try to locate by matching text
        if (!vendorEl && vendorText) {
          const candidates = Array.from(itemEl.querySelectorAll('*')).filter(n =>
            n.childElementCount === 0 && n.textContent && n.textContent.trim().length
          );
          const target = candidates.find(n => n.textContent.trim().toLowerCase() === vendorText.trim().toLowerCase());
          if (target) vendorEl = target;
        }

        if (!vendorEl) return;

        vendorEl.innerHTML = '';
        const badge = document.createElement('span');
        badge.className = 'ps-used-badge';
        badge.textContent = 'Used Item';
        vendorEl.appendChild(badge);

        itemEl.dataset.usedVendorReplaced = 'true';
      };

      // Prefer the JSON predictive endpoint for batch data
      try {
        const jsonParams = searchParamsForJson
          .replace(/&?section_id=predictive-search/g, '')
          .concat(`&${encodeURIComponent('resources[type]')}=product`);

        const jsonRes = await fetch(`${theme.routes.predictiveSearch}.json?${jsonParams}`, {
          headers: { 'Accept': 'application/json' }
        });
        if (!jsonRes.ok) throw new Error('JSON predictive search failed');

        const data = await jsonRes.json();
        const products =
          (data?.resources?.results?.products) ||
          (data?.resources?.products?.results) ||
          (Array.isArray(data?.products) ? data.products : []) ||
          [];

        const byHandle = new Map();
        products.forEach(p => byHandle.set(p?.handle, p));

        await Promise.all(
          rendered.map(async ({ link, handle }) => {
            const itemEl =
              link.closest('.predictive-search__item') ||
              link.closest('li') ||
              link.parentElement;

            let product = byHandle.get(handle);

            // Fallback: fetch product if JSON didn’t include it
            if (!product) {
              try {
                const res = await fetch(`/products/${handle}.js`, { headers: { 'Accept': 'application/json' } });
                if (res.ok) product = await res.json();
              } catch (_) { /* ignore */ }
            }

            if (!product) return;

            const tags = Array.isArray(product.tags)
              ? product.tags
              : (typeof product.tags === 'string' ? product.tags.split(',').map(t => t.trim()) : []);

            if (tags.includes('SC-USED')) {
              replaceVendorWithBadge(itemEl, product.vendor || '');
            }
          })
        );
      } catch (_) {
        // If JSON route fails, per-product fallback
        await Promise.all(
          rendered.map(async ({ link, handle }) => {
            const itemEl =
              link.closest('.predictive-search__item') ||
              link.closest('li') ||
              link.parentElement;

            try {
              const res = await fetch(`/products/${handle}.js`, { headers: { 'Accept': 'application/json' } });
              if (!res.ok) return;
              const product = await res.json();
              const tags = Array.isArray(product.tags)
                ? product.tags
                : (typeof product.tags === 'string' ? product.tags.split(',').map(t => t.trim()) : []);
              if (tags.includes('SC-USED')) {
                replaceVendorWithBadge(itemEl, product.vendor || '');
              }
            } catch (e) { /* ignore */ }
          })
        );
      }
    }
  }

  customElements.define('predictive-search', PredictiveSearch);
}
